<template>
  <a-descriptions
    :column="{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }"
    :style="{marginTop: '.5rem'}"
    size="small"
    bordered>
    <a-descriptions-item :label="getLocaleText('status')">
      <a-row :style="{ display: 'flex', alignItems: 'center' }">
        <status-tag :status="getStatusTag(plan.status)" />
        <a-spin
          v-if="plan.status === 10"
          :style="{ marginLeft: '.5rem' }"
          size="small" />
      </a-row>
    </a-descriptions-item>

    <a-descriptions-item label="Статус отправки">
      <plan-send-status
        v-if="plan.id"
        :key="plan.id"
        :plan-id="plan.id"
        :revision="`${plan.status}:${plan.status_3d}:${plan.plan_id}`" />
    </a-descriptions-item>

    <a-descriptions-item
      :label="getLocaleText('plan-number-in-apartment')">
      <a-tag
        v-if="plan.apartment"
        color="default">
        {{ plan.apartment.plan_number_in_apartment ?? '' }}
      </a-tag>
      <span v-else>Отсутствует</span>
    </a-descriptions-item>

    <a-descriptions-item
      :label="getLocaleText('priority')">
      <span v-if="plan.priority != null">
        {{ planPriorityNames[plan.priority] }}
      </span>
      <span v-else>Обычный</span>
    </a-descriptions-item>

    <a-descriptions-item
      :label="getLocaleText('cutter')">
      <div :style="{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }">
        <a-span :style="{marginRight: '.5rem'}">
          {{ plan.cutter ? plan.cutter.name : "Отсутствует" }}
        </a-span>
        <a-button
          v-if="canChangeAssignees"
          @click="openChangeCutterModal">
          <template #icon>
            <edit-outlined />
          </template>
        </a-button>
      </div>
    </a-descriptions-item>

    <a-descriptions-item
      :label="getLocaleText('ninja')">
      <div :style="{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }">
        <a-span :style="{marginRight: '.5rem'}">
          {{ plan.ninja ? plan.ninja.name : "Отсутствует" }}
        </a-span>
        <a-button
          v-if="canChangeNinja"
          @click="openChangeNinjaModal">
          <template #icon>
            <edit-outlined />
          </template>
        </a-button>
      </div>
    </a-descriptions-item>

    <a-descriptions-item
      :label="getLocaleText('manager')">
      <div :style="{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }">
        <a-span :style="{marginRight: '.5rem'}">
          {{ plan.manager ? plan.manager.name : "Отсутствует" }}
        </a-span>
        <a-button
          v-if="canChangeAssignees"
          @click="openChangeManagerModal">
          <template #icon>
            <edit-outlined />
          </template>
        </a-button>
      </div>
    </a-descriptions-item>

    <a-descriptions-item
      label="Проверяющий">
      <div :style="{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }">
        <a-span :style="{marginRight: '.5rem'}">
          {{ plan.reviewer ? plan.reviewer.name : "Отсутствует" }}
        </a-span>
        <a-button
          v-if="canChangeReviewer"
          @click="openChangeReviewerModal">
          <template #icon>
            <edit-outlined />
          </template>
        </a-button>
      </div>
    </a-descriptions-item>

    <a-descriptions-item label="Дедлайн">
      <a-space>
        <a-tag :color="deadlineColor">
          {{ deadlineText }}
        </a-tag>
        <a-button
          v-if="canEditDeadline"
          @click="openDeadlineModal">
          <template #icon>
            <edit-outlined />
          </template>
        </a-button>
      </a-space>
    </a-descriptions-item>

    <a-descriptions-item
      :label="getLocaleText('merged')">
      {{ isMerged ? 'Да' : 'Нет' }}
    </a-descriptions-item>

    <a-descriptions-item :label="getLocaleText('merged-id')">
      <span v-if="!isMerged">-</span>
      <a
        v-else
        target="_blank"
        :href="`${routeForCrmPlan}${plan.merged_from_crm_plan_id}`">{{ plan.merged_from_crm_plan_id }}</a>
    </a-descriptions-item>

    <a-descriptions-item
      v-if="optionsTags"
      :span="plan.external_id ? 1 : 2"
      :label="getLocaleText('tags')">
      <crm-plan-tags-select
        :data="{
          crm_tags: plan.crm_tags,
          crmTagsModified: Array.isArray(plan.crmTagsModified) ? plan.crmTagsModified : [],
        }"
        :tags-list="tagsList"
        @attach="attachTagsToPlan" />
    </a-descriptions-item>

    <a-descriptions-item
      v-if="!optionsTags"
      :span="plan.external_id ? 1 : 2"
      :label="getLocaleText('tags')">
      <crm-plan-tags-select
        :data="{
          crm_tags: plan.crm_tags,
          crmTagsModified: plan.crm_tags.map((t) => t.id),
        }"
        :tags-list="tagsList"
        @attach="attachTagsToPlan" />
    </a-descriptions-item>

    <a-descriptions-item
      v-if="plan.external_id"
      label="ID у клиента">
      <a-tag color="default">
        {{ plan.external_id }}
      </a-tag>
    </a-descriptions-item>

    <a-descriptions-item
      v-if="originalPlan"
      :label="getLocaleText('original-plan')">
      <a :href="`/crm/plans/${originalPlan.id}`">
        {{ originalPlan.name }}
      </a>
    </a-descriptions-item>

    <a-descriptions-item
      v-if="originalPlan"
      :label="getLocaleText('original-plan-status')">
      <status-tag :status="getStatusTag(Number(originalPlan.status))" />
    </a-descriptions-item>

    <a-descriptions-item
      :label="getLocaleText('region')">
      <editable-field
        :value="currentPlan.region"
        @save-data="editRegion" />
    </a-descriptions-item>

    <a-descriptions-item
      :label="getLocaleText('developer')">
      <editable-field
        :value="currentPlan.developer"
        @save-data="editDeveloper" />
    </a-descriptions-item>

    <a-descriptions-item
      :label="getLocaleText('complex')">
      <editable-field
        :value="currentPlan.complex"
        @save-data="editComplex" />
    </a-descriptions-item>

    <a-descriptions-item
      :label="getLocaleText('address')">
      <editable-field
        :value="currentPlan.address"
        @save-data="editAddress" />
    </a-descriptions-item>

    <a-descriptions-item
      :label="getLocaleText('individual-style')">
      <span v-if="currentPlan.style_detection_rule === 'developer' && currentPlan.developer">
        {{ currentPlan.developer }}
      </span>

      <span v-else-if="currentPlan.style_detection_rule === 'developer' && currentPlan.developer === null">
        Да
      </span>

      <span v-else>
        Нет
      </span>
    </a-descriptions-item>

    <a-descriptions-item
      :label="getLocaleText('area')">
      {{ plan?.plan?.additional?.area ? plan.plan.additional.area + ' м²' : 'Отсутствует' }}
    </a-descriptions-item>
  </a-descriptions>

  <a-modal
    v-model:open="isDeadlineModalVisible"
    title="Изменить дедлайн планировки"
    ok-text="Сохранить"
    cancel-text="Отмена"
    :confirm-loading="isDeadlineSaving"
    @ok="saveDeadline">
    <a-date-picker
      v-model:value="deadlineDraft"
      show-time
      allow-clear
      style="width: 100%" />
  </a-modal>

  <a-divider>Параметры рендеринга</a-divider>

  <a-descriptions
    :column="{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }"
    :style="{marginTop: '.5rem'}"
    size="small"
    bordered>
    <a-descriptions-item
      :label="getLocaleText('has-panorama')">
      {{ plan.has_panorama ? "Да" : "Нет" }}
    </a-descriptions-item>

    <a-descriptions-item
      v-if="isNewPlanOptionsVisible"
      :label="getLocaleText('ruler')">
      {{ plan.capabilities?.ruler?.enabled ? "Есть" : "Нет" }}
    </a-descriptions-item>

    <a-descriptions-item
      v-if="isNewPlanOptionsVisible"
      :label="getLocaleText('interactive-top-view')">
      {{ plan.capabilities?.interactive_top_view?.enabled ? "Есть" : "Нет" }}
    </a-descriptions-item>

    <a-descriptions-item
      v-if="isNewPlanOptionsVisible"
      :label="getLocaleText('top-view-as-mini-plan')">
      {{ plan?.capabilities?.top_view_as_miniplan?.enabled ? "Есть" : "Нет" }}
    </a-descriptions-item>

    <a-descriptions-item
      v-if="isNewPlanOptionsVisible"
      :label="getLocaleText('hidden-furniture')">
      {{ plan.capabilities?.hidden_furniture?.enabled ? "Да" : "Нет" }}
    </a-descriptions-item>

    <a-descriptions-item
      v-if="isNewPlanOptionsVisible"
      :label="getLocaleText('projection-mode')">
      <span v-if="plan?.plan?.options?.projection_mode === 'perspective'">
        {{ 'Перспектива' }}
      </span>

      <span v-else>
        {{ 'Ортография' }}
      </span>
    </a-descriptions-item>

    <a-descriptions-item
      v-if="isNewPlanOptionsVisible"
      :label="getLocaleText('camera-navigation')">
      <span v-if="plan?.capabilities?.camera_navigation === 'portals'">
        {{ 'Порталы' }}
      </span>

      <span v-else>
        {{ 'Классический режим' }}
      </span>
    </a-descriptions-item>

    <a-descriptions-item
      v-if="isNewPlanOptionsVisible"
      :label="getLocaleText('transparent-background')">
      {{ plan?.plan?.options?.middle_cut_render_modes?.color_masked ? "Да" : "Нет" }}
    </a-descriptions-item>

    <a-descriptions-item
      v-if="isNewPlanOptionsVisible"
      :label="getLocaleText('rtx-quality')">
      {{ rtxQuality }}
    </a-descriptions-item>

    <a-descriptions-item
      v-if="isNewPlanOptionsVisible"
      :label="getLocaleText('is-webp-enabled')">
      {{ plan?.plan?.options?.is_webp_enabled ? "Webp" : "JPG" }}
    </a-descriptions-item>

    <a-descriptions-item
      v-if="isNewPlanOptionsVisible && plan?.plan?.options?.lossless_format"
      :label="getLocaleText('selected-lossless-format')">
      {{ LosslessFormatNames[plan?.plan?.options?.lossless_format] }}
    </a-descriptions-item>

    <a-descriptions-item
      v-if="isNewPlanOptionsVisible && plan?.plan?.options?.panoramas_type"
      :label="getLocaleText('selected-lossless-format')">
      {{ PanoramasTypeNames[plan?.plan?.options?.panoramas_type] }}
    </a-descriptions-item>

    <a-descriptions-item
      v-if="isNewPlanOptionsVisible"
      :label="getLocaleText('unreal-top-views-capture')">
      {{ plan?.plan?.options?.is_unreal_top_views_capture_enabled ? "Да" : "Нет" }}
    </a-descriptions-item>

    <a-descriptions-item
      v-if="isNewPlanOptionsVisible"
      :label="getLocaleText('unreal-top-down-views-capture')">
      {{ plan?.plan?.options?.is_unreal_top_down_views_capture_enabled ? "Да" : "Нет" }}
    </a-descriptions-item>

    <a-descriptions-item
      v-if="isNewPlanOptionsVisible"
      :label="getLocaleText('unreal-top-down-view-count')">
      {{ plan?.plan?.options?.unreal_top_down_view_count }}
    </a-descriptions-item>

    <a-descriptions-item
      v-if="isNewPlanOptionsVisible"
      :label="getLocaleText('unreal-middle-cut-capture')">
      {{ plan?.plan?.options?.is_unreal_middle_cut_capture_enabled ? "Да" : "Нет" }}
    </a-descriptions-item>

    <a-descriptions-item
      v-if="isNewPlanOptionsVisible"
      :label="getLocaleText('apply-middle-cut-in-top-view')">
      {{ plan?.plan?.options?.apply_middle_cut_in_top_view ? "Да" : "Нет" }}
    </a-descriptions-item>

    <a-descriptions-item
      :label="getLocaleText('override-node-label')">
      {{ plan?.plan?.options?.override_node_label ? plan?.plan?.options?.override_node_label : "Не указан" }}
    </a-descriptions-item>
  </a-descriptions>
  <a-divider />

  <a-divider>Параметры расстановки</a-divider>

  <a-descriptions
    :column="{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }"
    :style="{marginTop: '.5rem'}"
    size="small"
    bordered>
    <a-descriptions-item
      v-if="isNewPlanOptionsVisible"
      :label="getLocaleText('is-furniture-enabled')">
      {{ plan?.plan?.options?.is_furniture_enabled ? "Да" : "Нет" }}
    </a-descriptions-item>
    <a-descriptions-item
      v-if="isNewPlanOptionsVisible"
      :label="getLocaleText('is-furniture-lights-enabled')">
      {{ plan?.plan?.options?.is_furniture_lights_enabled ? "Да" : "Нет" }}
    </a-descriptions-item>
    <a-descriptions-item
      v-if="isNewPlanOptionsVisible"
      :label="getLocaleText('is-furniture-glass-walls-collapse-enabled')">
      {{ plan?.plan?.options?.is_furniture_glass_walls_collapse_enabled ? "Да" : "Нет" }}
    </a-descriptions-item>
    <a-descriptions-item
      v-if="isNewPlanOptionsVisible"
      :label="getLocaleText('is-furniture-curtains-radiators-enabled')">
      {{ plan?.plan?.options?.is_furniture_curtains_radiators_enabled ? "Да" : "Нет" }}
    </a-descriptions-item>
  </a-descriptions>
  <a-divider />
</template>

<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { message } from 'ant-design-vue';

import CrmPlanTagsSelect from '@/Components/Service/Plans/CrmPlanTagsSelect.vue';
import { EditOutlined } from '@ant-design/icons-vue';
import EditableField from '@/Components/Common/EditableField.vue';
import { planPriorityNames } from '@/enums/planPriority';
import { getStatusTag } from '@/enums/PlanStatusEnum';
import { RtxPreset } from '@/enums/RtxPreset';
import PlanSendStatus from '@/Components/CRM/Plans/PlanSendStatus.vue';
import StatusTag from '@/Components/Common/StatusTag.vue';
import getFlag from '@/helpers/getFlag';
import localeStore from '@/store/locale';
import { LosslessFormatNames } from '@/enums/losslessFormatOptions';
import { PanoramasTypeNames } from '@/enums/PanoramasType';
import dayjs from 'dayjs';

const props = defineProps({
  routeForCrmPlan: {
    type: String,
    default: () => '/crm/plans/',
  },
  attachTagsToPlan: {
    type: Function,
    default: () => null,
  },
  plan: {
    type: Object,
    default: () => ({}),
  },
  planData: {
    type: Object,
    default: () => ({}),
  },
  canChangeAssignees: {
    type: Boolean,
    default: false,
  },
  canChangeNinja: {
    type: Boolean,
    default: false,
  },
  canChangeReviewer: {
    type: Boolean,
    default: false,
  },
  tagsList: {
    type: Object,
    default: () => ({}),
  },
  optionsTags: {
    type: Boolean,
    default: false,
  },
  isMerged: {
    type: Boolean,
    default: false,
  },
  unrealRtxPreset: {
    type: Number,
    default: -1,
  },
});

const store = useStore();
const canEditTags = computed(() => store.getters['auth/hasPermission']('api.crm.plans.sync_tags'));
const canEditDeadline = computed(() => store.getters['auth/hasPermission']('api.crm.plans.update_deadline'));
const currentPlan = computed(() => store.state.crm.currentPlan);
const isNewPlanOptionsVisible = getFlag('isNewPlanOptionsVisible');

const { getLocaleText } = localeStore();

const emit = defineEmits(['change-strategic-data', 'deadline-changed']);

const openChangeNinjaModal = () => store.commit('crm/setChangeNinjaModalVisibility', true);
const openChangeCutterModal = () => store.commit('crm/setChangeCutterModalVisibility', true);
const openChangeManagerModal = () => store.commit('crm/setChangeManagerModalVisibility', true);
const openChangeReviewerModal = () => store.commit('crm/setChangeReviewerModalVisibility', true);
const originalPlan = computed(() => store.state.crm.currentPlan.original_crm_plan);
const isDeadlineModalVisible = ref(false);
const isDeadlineSaving = ref(false);
const deadlineDraft = ref(null);

const deadlineHoursLeft = computed(() => {
  if (!props.plan.deadline_at) return null;
  return (new Date(props.plan.deadline_at).getTime() - Date.now()) / 3_600_000;
});

const deadlineColor = computed(() => {
  if (deadlineHoursLeft.value === null) return 'default';
  if (deadlineHoursLeft.value < 0) return 'red';
  if (deadlineHoursLeft.value < 6) return 'red';
  if (deadlineHoursLeft.value < 24) return 'orange';
  return 'green';
});

const deadlineText = computed(() => {
  if (!props.plan.deadline_at) return 'Не задан';
  const suffix = props.plan.deadline_overridden ? ' · вручную' : ' · от апартамента';
  return `${dayjs(props.plan.deadline_at).format('DD.MM.YYYY HH:mm')}${suffix}`;
});

const openDeadlineModal = () => {
  deadlineDraft.value = props.plan.deadline_at ? dayjs(props.plan.deadline_at) : null;
  isDeadlineModalVisible.value = true;
};

const saveDeadline = async () => {
  isDeadlineSaving.value = true;
  try {
    await store.dispatch('crm/updateCrmPlanDeadline', {
      id: props.plan.id,
      deadlineAt: deadlineDraft.value ? deadlineDraft.value.toISOString() : null,
    });
    message.success('Дедлайн обновлен');
    isDeadlineModalVisible.value = false;
    emit('deadline-changed');
  } catch (e) {
    message.error('Не удалось обновить дедлайн');
  } finally {
    isDeadlineSaving.value = false;
  }
};

const rtxQuality = computed(() => Object.keys(RtxPreset)
  .find((key) => RtxPreset[key] === props.unrealRtxPreset) || 'Не указано');

const editRegion = (newValue) => {
  store.dispatch('crm/changeStrategicData', {
    id: currentPlan.value.id,
    region: newValue,
  });
  emit('change-strategic-data');
};

const editDeveloper = (newValue) => {
  store.dispatch('crm/changeStrategicData', {
    id: currentPlan.value.id,
    developer: newValue,
  });
  emit('change-strategic-data');
};

const editComplex = (newValue) => {
  store.dispatch('crm/changeStrategicData', {
    id: currentPlan.value.id,
    complex: newValue,
  });
  emit('change-strategic-data');
};

const editAddress = (newValue) => {
  store.dispatch('crm/changeStrategicData', {
    id: currentPlan.value.id,
    address: newValue,
  });
  emit('change-strategic-data');
};

</script>
